import type { DiscountType, InvoiceLine } from "@/types/invoice";

export const DISCOUNT_PERCENTAGE: DiscountType = "PERCENTAGE";
export const DISCOUNT_VALUE: DiscountType = "VALUE";

type DiscountedLine = Pick<InvoiceLine, "unit_price" | "quantity" | "discount">;

/**
 * The discount mode lives on the INVOICE, not on the line: Siigo fixes it per
 * document, so a mixed invoice would show one thing on screen and another in the
 * ERP. Each line still carries its own discount figure in `discount` — what the
 * document decides is the UNIT of that figure, a percentage or an amount.
 */
export function isValueDiscount(discountType?: DiscountType): boolean {
  return discountType === DISCOUNT_VALUE;
}

/** price * quantity, before any discount. */
export function getLineGross(line: DiscountedLine): number {
  return (line.unit_price || 0) * (line.quantity || 1);
}

/**
 * The absolute amount taken off the line.
 *
 * In VALUE mode `discount` already IS that amount, and it is the authoritative
 * figure: Siigo rounds discounts to whole pesos, so a discount with cents can
 * only be reproduced by sending the amount, never the percentage.
 *
 * Derived as gross - net rather than gross * pct / 100. Both are the same number
 * in decimal, but only the subtraction keeps `gross - amount === net` exactly in
 * floating point (600/600 sampled combinations against 470/600), which is what
 * makes switching the invoice to VALUE mode leave every line total untouched.
 */
export function getDiscountAmount(line: DiscountedLine, discountType?: DiscountType): number {
  if (isValueDiscount(discountType)) return line.discount || 0;
  return getLineGross(line) - getLineNet(line, discountType);
}

/** The percentage equivalent of the line discount, whichever mode the invoice is in. */
export function getDiscountPercentage(line: DiscountedLine, discountType?: DiscountType): number {
  if (!isValueDiscount(discountType)) return line.discount || 0;
  const gross = getLineGross(line);
  if (!gross) return 0;
  return (line.discount || 0) / gross * 100;
}

/**
 * Net of an invoice line: price * quantity, less the line discount.
 *
 * Single source of truth for this formula. It used to be copy-pasted across the
 * totals composables and the line editors, which is how they drifted on their
 * edge-case defaults. Import it — do not inline the arithmetic again.
 *
 * The order of operations in the percentage branch is load-bearing, and this one
 * is not a matter of accuracy: measured against the exact decimal value neither
 * associativity wins (roughly 54/46), but they disagree on the cent itself in
 * about 1% of lines with a discount. The number that reaches the ERP is produced
 * by the API and the middleware, and both use exactly this form, so the frontend
 * matches the backend rather than leading it. Do not "simplify" it to
 * `gross * (100 - d) / 100`. Pinned by `src/__tests__/utils/invoiceLineNet.test.ts`.
 */
export function getLineNet(line: DiscountedLine, discountType?: DiscountType): number {
  const gross = getLineGross(line);
  if (isValueDiscount(discountType)) return gross - (line.discount || 0);
  return gross * (1 - (line.discount || 0) / 100);
}
